/** Minimal column_meta slice for picker visibility checks. */
export type MetricPickerColumnMeta = {
  icon_name?: string | null;
};

/**
 * Metrics with 0% UI-displayable values in stocks_clean (see
 * sql/metrics_null_audit.sql → WHERE displayable = 0). Hidden from pickers
 * and All Metrics until the ETL pipeline is fixed. Remove fields from this
 * set once audit shows displayable_pct > 0.
 */
export const METRICS_TEMPORARILY_HIDDEN = new Set<string>([
  "Div3Yexpgrowth",
  "DivdendYield3Y",
]);

/**
 * Extra fields hidden only from HeroMetricPicker (still shown on All Metrics /
 * dashboards). Used for near-duplicates that clutter the add-column UX.
 */
export const METRICS_HIDDEN_FROM_PICKER = new Set<string>([
  // Duplicate of eps_forecast_fy* (same source in peg_pipeline). Keep the
  // snake_case Financials estimates in the picker; hide the stocks5 copies.
  "EPSFY1",
  "EPSFY2",
  "EPSFY3",
  // Legacy stocks5 MarketCap — picker uses marketcap_ltm (Financials, $B).
  "MarketCap",
  // company_historicals LTM margins duplicate stocks5 Quality margins
  // (EbitdaMargin / EbitMargin / NetIncomeMargin) and often render as na
  // (null/0 + zeroAsDash). Keep the scored Quality fields in the picker.
  "ebitdamargin_ltm",
  "ebitmargin_ltm",
  "nimargin_ltm",
  // company_historicals pe_ltm duplicates stocks5 PE (trailing) and usually
  // renders as na. Keep Valuation · P/E Ratio (Trailing).
  "pe_ltm",
  // company_historicals pe_forecast_fy* ("P/E Est FY*") duplicate scored
  // Valuation fields PEFY1 / PEFY2 / PE3Y and usually render as na.
  "pe_forecast_fy1",
  "pe_forecast_fy2",
  "pe_forecast_fy3",
  // company_historicals raw forecast margins (* Est FY*) duplicate the scored
  // Quality outlook fields (EBITDAMarginForecastFY* etc.) and usually render
  // as na. Keep Quality · Margin outlook only.
  "ebitdamargin_forecast_fy1",
  "ebitdamargin_forecast_fy2",
  "ebitdamargin_forecast_fy3",
  "ebitmargin_forecast_fy1",
  "ebitmargin_forecast_fy2",
  "ebitmargin_forecast_fy3",
  "nimargin_forecast_fy1",
  "nimargin_forecast_fy2",
  "nimargin_forecast_fy3",
]);

export function isMetricTemporarilyHidden(metricKey: string): boolean {
  return METRICS_TEMPORARILY_HIDDEN.has(metricKey);
}

/**
 * Metrics tagged with the Info icon in column_meta are internal / legacy fields
 * (e.g. stocks_clean.MarketCap for sizing). They should not appear in
 * HeroMetricPicker — use the categorized Financials entry instead
 * (marketcap_ltm → "Market Cap, $B").
 */
export function isMetricVisibleInPicker(
  metricKey: string,
  columnMeta: Record<string, MetricPickerColumnMeta>,
): boolean {
  if (isMetricTemporarilyHidden(metricKey)) return false;
  if (METRICS_HIDDEN_FROM_PICKER.has(metricKey)) return false;
  return columnMeta[metricKey]?.icon_name !== "Info";
}

export function filterMetricsForPicker<T extends string>(
  metrics: readonly T[],
  columnMeta: Record<string, MetricPickerColumnMeta>,
): T[] {
  return metrics.filter((key) => isMetricVisibleInPicker(key, columnMeta));
}
